import React, { useEffect, useState } from 'react';
import { EditOrganizationForm } from '../organization-form/edit-organization-form';
import { DialogBox } from '../../dialog-box/dialog-box';
import { useAuthState } from "../../../utilities/contexts/auth-state-context";
import UserUtil from '../../../utilities/user-util';
import OrganizationUtil, { AvailableLimits } from '../../../utilities/organization-util';
import OrganizationService from '../../../utilities/services/organization-service';
import { Organization } from '../../../models/interfaces/organization';
import { SubOrganizationModal } from '../sub-organization-modal/sub-organization-modal';
import { createLogger } from '../../../utilities/logger';
import { useTranslation } from 'react-i18next';

const log = createLogger("EditOrganizationModal");

type EditOrganizationModalProps = {
    organizationId: string | undefined,
    open: boolean,
    onClose: Function,
    /** Pre-loaded parent (head) organization, forwarded to the sub-organization form. */
    parentOrganization?: Organization,
    /** Pre-computed remaining capacity, forwarded to the sub-organization form. */
    availableLimits?: AvailableLimits,
};

export const EditOrganizationModal: React.FC<EditOrganizationModalProps> = (props: EditOrganizationModalProps) => {
    const {
        organizationId,
        open,
        onClose,
        parentOrganization,
        availableLimits,
    } = props;

    const { state } = useAuthState();
    const { t } = useTranslation();

    const [isSubOrg, setIsSubOrg] = useState<boolean>();
    const [organization, setOrganization] = useState<Organization>();
    const [loadedOrganizationId, setLoadedOrganizationId] = useState<string>();

    const isAccountHolder = UserUtil.isAccountHolder(state.user);

    useEffect(() => {
        setOrganization(undefined);
        setLoadedOrganizationId(undefined);

        if (organizationId) {
            OrganizationService.get(organizationId).then((response) => {
                setOrganization(response ?? undefined);
                setIsSubOrg(OrganizationUtil.isSubOrganization(response));
                setLoadedOrganizationId(organizationId);
            }).catch((e) => {
                log.error("Failed to load organization for edit", {
                    exception: e,
                    organizationId,
                });
            });
        } else {
            setIsSubOrg(false);
        }
    }, [organizationId])

    // Wait for the organization of the current id so the form never mounts
    // with stale values or flashes create-mode copy.
    if (isSubOrg === undefined || (organizationId != null && loadedOrganizationId !== organizationId)) {
        return null;
    }

    if (isSubOrg || isAccountHolder) {
        return (
            <SubOrganizationModal
                open={!!organizationId && open}
                onClose={() => onClose()}
                title={t('organization.edit.subOrgTitle')}
                organizationId={organizationId}
                organization={organization}
                parentOrganization={parentOrganization}
                availableLimits={availableLimits}
                onSaved={() => onClose()}
            />
        );
    }

    return (
        <DialogBox
            maxWidth="lg"
            fullWidth
            open={!!organizationId && open}
            onClose={onClose}
            sx={{
                maxWidth: '1400px',
                height: '1200px',
                margin: 'auto',
                maxHeight: '99ch',
            }}
        >
            <EditOrganizationForm
                onSave={onClose}
                onCancel={onClose}
                organizationId={organizationId}
            />
        </DialogBox>
    );
}
